import { Fragment, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import omit from 'lodash-es/omit'

import { REQUEST_THREAD_ENTITY } from '../../../../constants/entity'
import { ScreenContainerRef } from '../../../../context'
import {
  useInitData,
  useModals,
  useRegisterReload,
  useUserData,
} from '../../../../hooks'
import { isStaffUser } from '../../../../utils/user'
import {
  EMAIL_ITEM_ID_KEY,
  useEmailListMessageCompletedHandlers,
} from '../../../Email'
import { MINIMIZED_MODAL_SCOPE } from '../../../Modal'
import { useMultipleViewTypeList } from '../../../MultipleViewTypeList'
import { useAsyncTable } from '../../../Table'
import {
  REQUEST_PROFILE_TAB_DOCUMENTS,
  REQUEST_PROFILE_TAB_EMAILS,
} from '../../RequestProfile.constants'
import { useRequestProfileContext } from '../../RequestProfile.hooks'
import {
  getRequestThreadsFilterStateApi,
  getRequestThreadsListApi,
  updateRequestThreadsTableConfig,
} from '../RequestProfileTabEmails.api'
import {
  REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_AUDIENCE,
  REQUEST_PROFILE_TAB_EMAILS_FILTER_STATE_SUB,
} from '../RequestProfileTabEmails.constants'
import {
  useRequestProfileTabEmailsAddButton,
  useRequestProfileTabEmailsFoldersUnreadCount,
  useRequestProfileTabEmailsSubTabUrl,
} from '../RequestProfileTabEmails.hooks'
import {
  getIsRequestProfileTabEmailsListEmpty,
  getRequestProfileTabEmailsActiveSubTabFromFilters,
  getRequestProfileTabEmailsInitialFilters,
  getRequestProfileTabEmailsListViewType,
  getRequestProfileTabEmailsPermanentFilters,
  getRequestProfileTabEmailsSubMenu,
  getRequestProfileTabEmailsThreadPageLink,
  isRequestProfileTabEmailsUnreadFilterActive,
  shouldResetRequestProfileTabEmailsFiltersOnSearch,
} from '../RequestProfileTabEmails.utils'
import { RequestProfileTabEmailsCreateEmailPopUp } from '../RequestProfileTabEmailsCreateEmailPopUp'
import { RequestProfileTabEmailsSubTabs } from '../RequestProfileTabEmailsSubTabs'
import { RequestProfileTabEmailsTable } from '../RequestProfileTabEmailsTable'
import { RequestProfileTabEmailsToolbar } from '../RequestProfileTabEmailsToolbar'

import { REQUEST_PROFILE_TAB_EMAILS_MODALS_CONFIG } from './RequestProfileTabEmailsContent.constants'
import {
  useRequestProfileTabEmailsInitialThreadModal,
  useRequestProfileTabEmailsListViewType,
  useRequestProfileTabEmailsSearch,
  useRequestProfileTabEmailsSearchResult,
  useRequestProfileTabEmailsThreadPreviewPanel,
} from './RequestProfileTabEmailsContent.hooks'

import * as styles from './RequestProfileTabEmailsContent.module.css'

function RequestProfileTabEmailsContent({ requestId, onLoadCounters }) {
  const {
    isPreview,
    outboundUuid,
    registerEmailsRealtimeListener,
    registerEmailsTabReload,
    requestData,
    scrollWrapperRef,
  } = useRequestProfileContext()

  const [searchParams] = useSearchParams()

  const { availableSections } = useInitData()

  const currentUser = useUserData()

  const [isReloadingFoldersUnreadCount, setReloadingFoldersUnreadCount] =
    useState(false)

  const containerRef = useRef(null)

  const {
    closeEmailFormModal,
    closeModal,
    modals,
    setEmailFormModal,
    setModal,
  } = useModals(REQUEST_PROFILE_TAB_EMAILS_MODALS_CONFIG)

  const {
    handleChangeSearch,
    handleClearSearch,
    isSearchActive,
    search,
    searchFilters,
  } = useRequestProfileTabEmailsSearch()

  const filterStateApi = getRequestThreadsFilterStateApi(requestId)

  const listApi = getRequestThreadsListApi({ outboundUuid, requestId })

  function transformInitialFilters(filters) {
    return isPreview
      ? filters
      : getRequestProfileTabEmailsInitialFilters({ filters, searchParams })
  }

  const {
    availableFilters,
    columns,
    filterValues,
    handleLoadInit,
    handleReload,
    handleSetColumns,
    handleSetDense,
    handleSetFilterValues,
    handleSetPagination,
    handleSetPreview,
    handleSetSize,
    handleUpdateItem,
    isDense,
    isLoading,
    isRefetching,
    items,
    ordering,
    pagination,
    permissions,
    preview,
    requiredColumns,
    serverError,
  } = useAsyncTable({
    containerRef,
    entity: REQUEST_THREAD_ENTITY,
    extraFilters: searchFilters,
    filterStateApi,
    getPermanentFilters: getRequestProfileTabEmailsPermanentFilters,
    isLocalPagination: isPreview,
    itemIdKey: EMAIL_ITEM_ID_KEY,
    listApi,
    sub: REQUEST_PROFILE_TAB_EMAILS_FILTER_STATE_SUB,
    transformInitialFilters,
    withoutSubMenu: true,
  })

  useEffect(() => {
    handleLoadInit()
  }, [])

  const { handleResetSubTabAndFilters, handleSetActiveSubTab } =
    useRequestProfileTabEmailsSubTabUrl({
      filterValues,
      handleSetFilterValues,
      isPreview,
    })

  // The backend ignores sub-tabs and filters for a search, so the UI drops them too.
  async function handleChangeSearchWithFiltersReset(event) {
    const searchQuery = await handleChangeSearch(event)

    if (
      shouldResetRequestProfileTabEmailsFiltersOnSearch({
        filterValues,
        searchQuery,
      })
    ) {
      handleResetSubTabAndFilters()
    }
  }

  const audience =
    filterValues?.[REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_AUDIENCE] ?? null

  const subMenu = getRequestProfileTabEmailsSubMenu(filterValues)

  const filterValuesWithSearch = { ...filterValues, ...searchFilters }

  const { foldersUnreadCount, handleReloadFoldersUnreadCount } =
    useRequestProfileTabEmailsFoldersUnreadCount({
      audience,
      isEnabled: filterValues != null,
      outboundUuid,
      requestId,
    })

  async function handleReloadListAndCount() {
    setReloadingFoldersUnreadCount(true)

    await handleReloadFoldersUnreadCount()

    setReloadingFoldersUnreadCount(false)

    onLoadCounters?.([
      REQUEST_PROFILE_TAB_EMAILS,
      REQUEST_PROFILE_TAB_DOCUMENTS,
    ])

    return handleReload()
  }

  function handleSuccessRead(mailUuid) {
    handleUpdateItem({ id: mailUuid, updates: { unread: false } })
    handleReloadFoldersUnreadCount()
    onLoadCounters?.([REQUEST_PROFILE_TAB_EMAILS])
  }

  // Reading updates the row in place, so under the `unread` filter the read
  // rows are refetched away once the pop-up closes.
  function handleCloseThreadModal() {
    closeModal()

    if (isRequestProfileTabEmailsUnreadFilterActive(filterValues)) {
      handleReload()
    }
  }

  const { handleInitialSubscribe, handleMessageCompleted } =
    useEmailListMessageCompletedHandlers({
      handleUpdateItem,
      items: items?.items,
      onReload: handleReloadListAndCount,
    })

  useEffect(() => {
    registerEmailsRealtimeListener?.({
      onInitialSubscribe: handleInitialSubscribe,
      onMessageCompleted: handleMessageCompleted,
      onResync: handleReloadListAndCount,
    })

    return () => registerEmailsRealtimeListener?.(null)
  }, [handleInitialSubscribe, handleMessageCompleted, handleReloadListAndCount])

  useRegisterReload({
    reload: handleReloadListAndCount,
    onRegisterReload: registerEmailsTabReload,
  })

  useRequestProfileTabEmailsInitialThreadModal({
    filterValues: filterValuesWithSearch,
    isPreview,
    items,
    ordering,
    requestId,
    setModal,
    onCloseModal: handleCloseThreadModal,
    onReload: handleReloadListAndCount,
    onSuccessRead: handleSuccessRead,
  })

  // `view` is deliberately not forwarded: the preview toggle is a per-visit
  // choice held in the profile-level context, not a persisted table setting.
  function handleUpdateConfig(obj) {
    return updateRequestThreadsTableConfig({
      columns,
      preview,
      ...omit(obj, 'view'),
    })
  }

  const hasError = Boolean(serverError)

  const { isSearchResult } = useRequestProfileTabEmailsSearchResult({
    isSearchActive,
    items,
  })

  const { isEmailPreviewAvailable, selectedListViewType, setListViewType } =
    useRequestProfileTabEmailsListViewType({ isPreview })

  function getThreadPageLink(id) {
    return getRequestProfileTabEmailsThreadPageLink({
      availableSections,
      isStaff: isStaffUser(currentUser),
      mailUuid: id,
      outboundUuid,
      requestId,
    })
  }

  const {
    activeItem,
    guardPreviewAction,
    handleChangeFocusedRow,
    handleChangeViewType,
    handleClickRow,
    handleClosePreview,
    handleMouseDownRow,
    handlePreviewResizeEnd,
    layoutListViewType,
    profileLoadersRef,
    setActiveItem,
    shouldCloseActivePreviewRef,
  } = useMultipleViewTypeList({
    getProfileLink: getThreadPageLink,
    handleReload: handleReloadListAndCount,
    handleSetPreview,
    handleSetView: setListViewType,
    handleUpdateConfig,
    itemIdKey: EMAIL_ITEM_ID_KEY,
    items: items?.items,
    listViewType: selectedListViewType,
    minimizedModalScope: MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE,
    preview,
  })

  const isEmptyList = getIsRequestProfileTabEmailsListEmpty({
    isLoading,
    items,
  })

  // The empty/error placeholder has no toolbar, so the panel only stays for an
  // active item, whose release runs through the shared invalidation guard.
  const listViewType = getRequestProfileTabEmailsListViewType({
    activeItem,
    hasError,
    isEmptyList,
    selectedListViewType,
  })

  // The table keeps the selected view so a row click reopens a closed panel.
  const previewPanelListViewType = getRequestProfileTabEmailsListViewType({
    activeItem,
    hasError,
    isEmptyList,
    selectedListViewType: layoutListViewType,
  })

  const { previewPanel } = useRequestProfileTabEmailsThreadPreviewPanel({
    activeItem,
    className: styles.threadPreview,
    items: items?.items,
    listViewType: previewPanelListViewType,
    pagination,
    preview,
    profileLoadersRef,
    requestId,
    setActiveItem,
    onClosePreview: handleClosePreview,
    onGuardPreviewAction: guardPreviewAction,
    onPreviewResizeEnd: handlePreviewResizeEnd,
    onReloadList: handleReloadListAndCount,
    onSetPagination: handleSetPagination,
    onSuccessRead: handleSuccessRead,
  })

  function handleSendSuccess() {
    handleReloadListAndCount()
  }

  function handleDensityChange() {
    handleSetDense(prev => !prev)
  }

  function handleOpenEmailCreatePopUp({
    audience: popupAudience,
    initialValues,
  }) {
    setEmailFormModal(
      <RequestProfileTabEmailsCreateEmailPopUp
        isLeaveGuardEnabled
        audience={popupAudience}
        initialValues={initialValues}
        minimizedModalScope={MINIMIZED_MODAL_SCOPE.REQUEST_PROFILE_TAB}
        requestData={requestData}
        requestId={requestId}
        onClose={closeEmailFormModal}
        onSendSuccess={handleSendSuccess}
      />
    )
  }

  const { handleSelect: handleSelectRecipientType } =
    useRequestProfileTabEmailsAddButton({
      requestData,
      onCloseModal: closeModal,
      onOpenPopUp: handleOpenEmailCreatePopUp,
      onSetModal: setModal,
    })

  return (
    <>
      <ScreenContainerRef value={containerRef}>
        <div
          className={styles.RequestProfileTabEmailsContent}
          ref={containerRef}
        >
          <RequestProfileTabEmailsSubTabs
            activeSubTab={getRequestProfileTabEmailsActiveSubTabFromFilters(
              filterValues
            )}
            disabled={isSearchActive}
            foldersUnreadCount={foldersUnreadCount}
            onSetActiveSubTab={handleSetActiveSubTab}
          />
          <RequestProfileTabEmailsToolbar
            availableFilters={availableFilters}
            filterValues={filterValues}
            isFilterDisabled={isSearchActive}
            isSkeleton={isLoading}
            ordering={ordering}
            pagination={pagination}
            requestId={requestId}
            search={search}
            setFilterValues={handleSetFilterValues}
            onChangeSearch={handleChangeSearchWithFiltersReset}
            onClearSearch={handleClearSearch}
            onReload={handleReloadListAndCount}
            onSelectRecipientType={handleSelectRecipientType}
          />
          <RequestProfileTabEmailsTable
            activeItem={activeItem}
            columns={columns}
            filterValues={filterValuesWithSearch}
            handleReload={handleReloadListAndCount}
            handleSetColumns={handleSetColumns}
            handleSetPagination={handleSetPagination}
            handleSetSize={handleSetSize}
            handleToggleDense={handleDensityChange}
            handleUpdateConfig={handleUpdateConfig}
            hasError={hasError}
            isDense={isDense}
            isLoading={isLoading}
            isRefetching={isRefetching || isReloadingFoldersUnreadCount}
            isSearchResult={isSearchResult}
            items={items}
            listViewType={listViewType}
            ordering={ordering}
            pagination={pagination}
            permissions={permissions}
            profileLoadersRef={profileLoadersRef}
            requestId={requestId}
            requiredColumns={requiredColumns}
            scrollContainerRef={scrollWrapperRef}
            setActiveItem={setActiveItem}
            shouldCloseActivePreviewRef={shouldCloseActivePreviewRef}
            subMenu={subMenu}
            onChangeFocusedRow={handleChangeFocusedRow}
            onChangeViewType={
              isEmailPreviewAvailable ? handleChangeViewType : null
            }
            onClickRowPreview={handleClickRow}
            onCloseModal={closeModal}
            onCloseThreadModal={handleCloseThreadModal}
            onMouseDownRowPreview={handleMouseDownRow}
            onSetModal={setModal}
            onSuccessRead={handleSuccessRead}
          />
        </div>
      </ScreenContainerRef>
      {previewPanel}
      {modals.map(([id, element]) => (
        <Fragment key={id}>{element}</Fragment>
      ))}
    </>
  )
}

export default RequestProfileTabEmailsContent
